import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api, type Paginated } from '../lib/api';
import { enumLabel, useI18n } from '../lib/i18n';
import { usePeriod, type Period } from '../lib/period';
import { Badge, Card, CardHeader, EmptyState, PageHeader, Pagination, Table, Tabs, Td, Th, Tr } from '../components/ui';
import { KpiCard, PeriodLine, useNumber } from '../components/kit';
import { FilterRow, Loaded, Note, ParamSelect, SearchBox, StatusBadge, TextLink, useAmount, useCompany, useFilterOptions, usePercent } from '../components/b-shared';
import { RISK_TIERS, ProjectsTable, type ClientRow, type ProjectRow } from '../components/b-projects';

/**
 * Projects & Clients (CR-008 §9). Projects: a deterministic delivery tier with its reasons, progress as
 * tracked tasks done, support coverage, and Finance's invoices that name the project — no margin, since no
 * cost is attributed to a project. Clients: their tickets and service health as SLA compliance and reopens —
 * there is no satisfaction survey, so no satisfaction score — what they owe and paid, and their contracts.
 */

const TABS = ['projects', 'clients'] as const;
type Tab = (typeof TABS)[number];
const PROJECT_STATUSES = ['DRAFT', 'ACTIVE', 'TESTING', 'ON_HOLD', 'SUPPORT', 'COMPLETED', 'CANCELLED', 'ARCHIVED'];

interface ProjectsPayload extends Paginated<ProjectRow> {
  period: Period;
  counts: { total: number; active: number; support: number; atRisk: number; watch: number };
}

interface ClientsPayload extends Paginated<ClientRow> {
  period: Period;
}

export function ProjectsPage() {
  const { t } = useI18n();
  const { param, setParam } = usePeriod();
  const tab: Tab = (TABS as readonly string[]).includes(param('tab')) ? (param('tab') as Tab) : 'projects';
  return (
    <>
      <PageHeader title={t('projects.title')} subtitle={t('projects.subtitle')} />
      <Tabs tabs={TABS.map((id) => ({ id, label: t(`projects.tab.${id}`) }))} active={tab} onChange={(id) => setParam('tab', id === 'projects' ? null : id)} />
      <div className="mt-5">{tab === 'projects' ? <ProjectsTab /> : <ClientsTab />}</div>
    </>
  );
}

function ProjectsTab() {
  const { t, locale, localized } = useI18n();
  const { params, param, setParam, query } = usePeriod();
  const num = useNumber();
  const filters = useFilterOptions();
  const q = param('q');
  const status = param('status');
  const risk = param('risk');
  const clientId = param('clientId');
  const page = Math.max(1, Number(param('page')) || 1);
  const list = useQuery({
    queryKey: ['executive', 'projects', params, q, status, risk, clientId, page],
    queryFn: () => api.get<ProjectsPayload>('/executive/projects', { ...params, q, status, risk, clientId, page }),
    placeholderData: keepPreviousData,
  });
  return (
    <>
      <FilterRow>
        <SearchBox label={t('common.search')} placeholder={t('projects.searchPlaceholder')} />
        <ParamSelect param="status" label={t('common.status')} allLabel={t('projects.allStatuses')} options={PROJECT_STATUSES.map((s) => ({ value: s, label: enumLabel(s, locale) }))} />
        <ParamSelect param="risk" label={t('projects.col.risk')} allLabel={t('projects.allRisks')} options={RISK_TIERS.map((r) => ({ value: r, label: t(`projects.risk.${r}`) }))} />
        <ParamSelect
          param="clientId"
          label={t('common.client')}
          allLabel={t('projects.allClients')}
          options={(filters.data?.clients ?? []).map((c) => ({ value: c.id, label: `${c.code} · ${localized(c)}` }))}
        />
      </FilterRow>
      <Loaded query={list}>
        {(data) => (
          <div className="space-y-5">
            <PeriodLine period={data.period} />
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
              <KpiCard label={t('projects.count.total')} value={num(data.counts.total)} />
              <KpiCard label={t('projects.count.delivering')} value={num(data.counts.active)} hint={t('projects.count.deliveringHint')} />
              <KpiCard label={t('projects.count.support')} value={num(data.counts.support)} />
              <KpiCard label={t('projects.count.atRisk')} value={num(data.counts.atRisk)} tone={data.counts.atRisk > 0 ? 'error' : 'neutral'} hint={t('projects.count.atRiskHint')} />
              <KpiCard label={t('projects.count.watch')} value={num(data.counts.watch)} tone={data.counts.watch > 0 ? 'warning' : 'neutral'} hint={t('projects.count.watchHint')} />
            </div>
            {risk && <Note>{t('projects.countsBeforeRisk')}</Note>}
            <Card padded={false}>
              {data.items.length === 0 ? (
                <EmptyState title={t('projects.empty')} hint={t('common.noResultsHint')} />
              ) : (
                <ProjectsTable rows={data.items} query={query} />
              )}
              <Pagination page={data.page} pageCount={data.pageCount} total={data.total} onChange={(p) => setParam('page', String(p))} />
            </Card>
            <div className="space-y-1.5">
              <Note>{t('projects.progressNote')}</Note>
              <Note>{t('projects.moneyNote')}</Note>
              <Note>{t('projects.marginNote')}</Note>
            </div>
          </div>
        )}
      </Loaded>
    </>
  );
}

function ClientsTab() {
  const { t, localized } = useI18n();
  const { params, param, setParam, query } = usePeriod();
  const num = useNumber();
  const pct = usePercent();
  const amount = useAmount();
  const { baseCurrency } = useCompany();
  const cq = param('cq');
  const page = Math.max(1, Number(param('page')) || 1);
  const list = useQuery({
    queryKey: ['executive', 'clients', params, cq, page],
    queryFn: () => api.get<ClientsPayload>('/executive/clients', { ...params, q: cq, page }),
    placeholderData: keepPreviousData,
  });
  const search = query ? `?${query}` : '';
  return (
    <>
      <FilterRow>
        <SearchBox param="cq" label={t('common.search')} placeholder={t('clients.searchPlaceholder')} />
      </FilterRow>
      <Loaded query={list}>
        {(data) => (
          <div className="space-y-5">
            <PeriodLine period={data.period} />
            <Note>{t('clients.serviceNote')}</Note>
            <Card padded={false}>
              <div className="px-5 pt-5">
                <CardHeader title={t('clients.tableTitle')} subtitle={t('clients.tableSubtitle')} />
              </div>
              {data.items.length === 0 ? (
                <EmptyState title={t('clients.empty')} hint={t('common.noResultsHint')} />
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <Th>{t('common.client')}</Th>
                      <Th align="end">{t('clients.col.projects')}</Th>
                      <Th align="end">{t('clients.col.openTickets')}</Th>
                      <Th align="end">{t('clients.col.resolved')}</Th>
                      <Th align="end">{t('clients.col.reopened')}</Th>
                      <Th align="end">{t('clients.col.assignmentSla')}</Th>
                      <Th align="end">{t('clients.col.viewSla')}</Th>
                      <Th align="end">{t('clients.col.collected')}</Th>
                      <Th align="end">{t('clients.col.outstanding')}</Th>
                      <Th align="end">{t('clients.col.overdueInvoices')}</Th>
                      <Th>{t('clients.col.contracts')}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((r) => {
                      const currency = r.money.currency || baseCurrency;
                      return (
                        <Tr key={r.client.id}>
                          <Td className="min-w-[13rem]">
                            <span className="flex flex-wrap items-center gap-1.5">
                              <TextLink to={`/clients/${r.client.id}${search}`} className="font-medium">
                                {localized(r.client)}
                              </TextLink>
                              {r.client.status !== 'ACTIVE' && <StatusBadge status={r.client.status} />}
                            </span>
                            <span dir="ltr" className="block font-mono text-caption text-[var(--text-tertiary)]">
                              {r.client.code}
                            </span>
                          </Td>
                          <Td align="end" className="tabular">
                            {num(r.projects.total)}
                            {r.projects.delivering > 0 && <span className="block text-caption text-muted">{t('clients.delivering', { n: num(r.projects.delivering) })}</span>}
                          </Td>
                          <Td align="end" className="tabular">
                            {num(r.tickets.open)}
                          </Td>
                          <Td align="end" className="tabular">
                            {num(r.tickets.resolvedInPeriod)}
                          </Td>
                          <Td align="end" className={r.tickets.reopenedInPeriod > 0 ? 'tabular text-warning-fg dark:text-warning-dark' : 'tabular'}>
                            {num(r.tickets.reopenedInPeriod)}
                          </Td>
                          <Td align="end" className="tabular">
                            {pct(r.service.assignmentSlaPercent)}
                          </Td>
                          <Td align="end" className="tabular">
                            {pct(r.service.viewSlaPercent)}
                          </Td>
                          <Td align="end" className="tabular">
                            {amount(r.money.collectedInPeriod, baseCurrency)}
                          </Td>
                          <Td align="end">
                            {r.money.outstanding === null ? (
                              <span className="flex flex-col items-end">
                                <span className="text-warning-fg dark:text-warning-dark">{t('common.unknown')}</span>
                                {r.money.unconverted.length > 0 && <span className="text-caption text-muted">{t('projects.noRate', { currencies: r.money.unconverted.join(', ') })}</span>}
                              </span>
                            ) : (
                              <span className="tabular">{amount(r.money.outstanding, currency)}</span>
                            )}
                          </Td>
                          <Td align="end" className={r.money.overdueInvoices > 0 ? 'tabular text-error-fg dark:text-error-dark' : 'tabular'}>
                            {num(r.money.overdueInvoices)}
                          </Td>
                          <Td className="min-w-[9rem] text-caption">
                            {r.contracts.covering === 0 ? (
                              <span className="text-muted">{t('clients.noContract')}</span>
                            ) : (
                              <span className="flex flex-col">
                                <span>{t('clients.covering', { n: num(r.contracts.covering) })}</span>
                                {r.contracts.endingSoon > 0 && <Badge tone="warning">{t('clients.endingSoon', { n: num(r.contracts.endingSoon), days: num(r.contracts.endingSoonDays) })}</Badge>}
                              </span>
                            )}
                          </Td>
                        </Tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
              <Pagination page={data.page} pageCount={data.pageCount} total={data.total} onChange={(p) => setParam('page', String(p))} />
            </Card>
            <div className="space-y-1.5">
              <Note>{t('clients.slaNote')}</Note>
              <Note>{t('clients.moneyNote')}</Note>
            </div>
          </div>
        )}
      </Loaded>
    </>
  );
}
